import { useEffect, useMemo, useState } from "react";
import {
  fetchRiskFlowEconHistory,
  type RiskFlowEconHistoryRow,
} from "../../lib/riskflow-econ-history-api";
import {
  riskFlowEconAiRead,
  riskFlowEconAiReadToneClass,
  resultToneClass,
  type RiskFlowEconPrintAnalysis,
} from "../../lib/riskflow-econ-print-analysis";
import type { RiskFlowPostSurface } from "./RiskFlowPostCard";

interface RiskFlowEconHistoryFrameProps {
  analysis: RiskFlowEconPrintAnalysis;
  surface: RiskFlowPostSurface;
}

interface DisplayRow {
  date: string;
  actual: string;
  forecast: string;
  previous: string;
  tone?: string;
}

export function RiskFlowEconHistoryFrame({
  analysis,
  surface,
}: RiskFlowEconHistoryFrameProps) {
  const [history, setHistory] = useState<RiskFlowEconHistoryRow[]>([]);

  useEffect(() => {
    let active = true;
    fetchRiskFlowEconHistory(analysis.key)
      .then((rows) => {
        if (!active) return;
        setHistory(rows);
      })
      .catch(() => {
        if (!active) return;
        setHistory([]);
      });
    return () => {
      active = false;
    };
  }, [analysis.key]);

  const rows = useMemo(() => buildRows(analysis, history), [analysis, history]);
  const compact = surface === "mini";

  return (
    <div
      className={`flex ${frameHeight(surface)} flex-col px-3 py-3`}
      data-riskflow-econ-history={analysis.key}
      aria-label={`${analysis.title} econ history`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--fintheon-text)]/78">
            Econ History
          </p>
          <p className="mt-1 truncate text-[11px] font-medium text-[var(--fintheon-text)]/54">
            {analysis.title}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[0.95fr_1fr_1fr_1fr] gap-x-3 border-b border-[var(--fintheon-accent)]/8 pb-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/28">
        <span>Date</span>
        <span className="text-right">Actual</span>
        <span className="text-right">Forecast</span>
        <span className="text-right">Previous</span>
      </div>

      <div className="min-h-0 flex-1">
        {rows.map((row) => (
          <div
            key={`${row.date}-${row.actual}-${row.forecast}`}
            className={`grid grid-cols-[0.95fr_1fr_1fr_1fr] gap-x-3 border-b border-[var(--fintheon-accent)]/6 py-2 font-mono text-[10px] tabular-nums ${
              row.tone ?? "text-[var(--fintheon-text)]/62"
            } ${compact ? "py-1.5" : ""}`}
          >
            <span className="min-w-0 truncate text-[var(--fintheon-text)]/44">
              {row.date}
            </span>
            <span className="text-right">{row.actual}</span>
            <span className="text-right text-[var(--fintheon-text)]/45">
              {row.forecast}
            </span>
            <span className="text-right text-[var(--fintheon-text)]/35">
              {row.previous}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-[0.95fr_1fr_1fr] gap-3 border-t border-[var(--fintheon-accent)]/10 pt-3 font-mono tabular-nums">
        <Readout
          label="Print"
          value={formatResult(analysis.result)}
          className={resultToneClass(analysis.result)}
        />
        <Readout
          label="Sigma"
          value={formatSigma(analysis.sigma)}
          className="text-[var(--fintheon-accent)]"
        />
        <Readout
          label={analysis.instrument}
          value={formatDirectionalRead(analysis.directionalRead)}
          className={resultToneClass(analysis.directionalRead)}
        />
      </div>
      <FullWidthReadout
        label="AI Read"
        value={riskFlowEconAiRead(analysis)}
        className={riskFlowEconAiReadToneClass(analysis)}
        compact={compact}
      />
    </div>
  );
}

function Readout({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/30">
        {label}
      </p>
      <p className={`mt-1 truncate text-[12px] font-semibold ${className}`}>
        {value}
      </p>
    </div>
  );
}

function FullWidthReadout({
  label,
  value,
  className,
  compact,
}: {
  label: string;
  value: string;
  className: string;
  compact: boolean;
}) {
  return (
    <div
      className={`mt-2 border-t border-[var(--fintheon-accent)]/6 pt-2 font-mono tabular-nums ${
        compact ? "mt-1.5 pt-1.5" : ""
      }`}
    >
      <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/30">
        {label}
      </p>
      <p
        className={`mt-1 text-[12px] font-semibold leading-tight ${className}`}
      >
        {value}
      </p>
    </div>
  );
}

function buildRows(
  analysis: RiskFlowEconPrintAnalysis,
  history: RiskFlowEconHistoryRow[],
): DisplayRow[] {
  const prior = uniqueHistory(history)
    .filter((row) => !samePrint(row, analysis))
    .slice(0, 3)
    .reverse()
    .map((row) => ({
      date: formatDate(row.date),
      actual: formatHistoryValue(row.actual, analysis.actual?.unit),
      forecast: formatHistoryValue(row.forecast, analysis.forecast?.unit),
      previous: formatHistoryValue(row.previous, analysis.previous?.unit),
    }));

  return [
    ...prior,
    {
      date: formatDate(currentPrintDate(analysis)),
      actual: analysis.actual?.display ?? "—",
      forecast: analysis.forecast?.display ?? "—",
      previous: analysis.previous?.display ?? "—",
      tone: resultToneClass(analysis.result),
    },
  ];
}

function uniqueHistory(
  history: RiskFlowEconHistoryRow[],
): RiskFlowEconHistoryRow[] {
  const seen = new Set<string>();
  const rows: RiskFlowEconHistoryRow[] = [];
  for (const row of history) {
    const key = [
      row.date ?? "",
      row.actual ?? "",
      row.forecast ?? "",
      row.previous ?? "",
    ].join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(row);
  }
  return rows;
}

function samePrint(
  row: RiskFlowEconHistoryRow,
  analysis: RiskFlowEconPrintAnalysis,
): boolean {
  const actual = displayNumber(analysis.actual?.display);
  const forecast = displayNumber(analysis.forecast?.display);
  if (actual == null) return false;
  if (!close(row.actual, actual)) return false;
  return (
    forecast == null || row.forecast == null || close(row.forecast, forecast)
  );
}

function close(left: number | null, right: number): boolean {
  return (
    left != null &&
    Math.abs(left - right) <= Math.max(0.0001, Math.abs(right) * 0.0005)
  );
}

function displayNumber(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value.replace(/[a-z%]+$/i, "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHistoryValue(
  value: number | null,
  unit: string | null | undefined,
) {
  if (value == null) return "—";
  const decimals = Math.abs(value) >= 100 ? 1 : Math.abs(value) >= 10 ? 2 : 3;
  const rendered = value.toLocaleString("en-US", {
    maximumFractionDigits: decimals,
  });
  return `${rendered}${unit ?? ""}`;
}

function formatDate(value: string | null) {
  if (!value) return "stored";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year.slice(-2)}`;
}

function formatResult(value: RiskFlowEconPrintAnalysis["result"]) {
  return value === "inline"
    ? "In-line"
    : value[0].toUpperCase() + value.slice(1);
}

function formatDirectionalRead(
  value: RiskFlowEconPrintAnalysis["directionalRead"],
) {
  return value === "neutral"
    ? "Neutral"
    : value[0].toUpperCase() + value.slice(1);
}

function formatSigma(value: number | null) {
  return value == null ? "— / 6σ" : `${value.toFixed(1)}σ / 6σ`;
}

function frameHeight(surface: RiskFlowPostSurface): string {
  return surface === "mini" ? "h-40" : "h-72";
}

function currentPrintDate(analysis: RiskFlowEconPrintAnalysis): string {
  return (
    analysis.printedAt?.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
  );
}
